/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { PlayerState } from "../enums";
import type { IStandaloneMediaPlayer } from "../interfaces";
import type { ISubscription } from "../ISubscription";
import { SubscriptionModel } from "../SubscriptionModel";
import { Diagnostics, Log } from "../../logging";
import type { TStandalonePlaybackEvent } from "../types";

/**
 * Glue player for standalone media, implemented over a plain HTML media
 * element with no streaming library involved.
 *
 * "Standalone" means media played directly off a URL by the element itself,
 * outside the adaptive-streaming (Shaka) pipeline — a progressive file such as
 * the `.wav` carried by an EAS alert, rather than a DASH or HLS manifest.
 * Shaka Player parses manifests and drives a MediaSource, so it cannot play a
 * single progressive file; this class covers that gap.
 *
 * End of media is detected from the element's native `ended` event and
 * republished through {@link getPlaybackEventSubscription} so the UI can act
 * on it.
 *
 * This class touches only {@link mMediaEl} — the element the view reserves for
 * standalone playback. It never reaches the element, listeners or MediaSource
 * that main playback uses, which is what lets the two be stopped independently.
 */
export class StandaloneMediaPlayerImpl implements IStandaloneMediaPlayer {
  private static readonly TAG: string = "StandaloneMediaPlayerImpl";

  private static readonly EVENT_ENDED: string = "ended";
  private static readonly EVENT_ERROR: string = "error";
  private static readonly EVENT_PLAYING: string = "playing";

  private static readonly DEFAULT_ERROR_MESSAGE: string =
    "Standalone media playback failed";

  private mMediaEl: HTMLVideoElement | null = null;

  private mCurrentUrl: string = "";

  private mIsPlaying: boolean = false;

  private mPlaybackEventSubscription: SubscriptionModel<TStandalonePlaybackEvent> =
    new SubscriptionModel<TStandalonePlaybackEvent>();

  /**
   * Bound handler references. Stored so removeEventListener() is given the
   * same function identity addEventListener() received — an inline arrow
   * would leak the listener on destroy().
   */
  private mEndedHandler: (event: Event) => void;

  private mErrorHandler: (event: Event) => void;

  private mPlayingHandler: (event: Event) => void;

  public constructor() {
    this.mEndedHandler = this.onEnded.bind(this);
    this.mErrorHandler = this.onError.bind(this);
    this.mPlayingHandler = this.onPlaying.bind(this);
  }

  private log(
    level: "debug" | "info" | "warn" | "error",
    message: string,
    ...optionalParams: unknown[]
  ): void {
    Diagnostics[level](
      StandaloneMediaPlayerImpl.TAG,
      message,
      ...optionalParams
    );
    Log[level[0] as "d" | "i" | "w" | "e"](
      StandaloneMediaPlayerImpl.TAG,
      message,
      ...optionalParams
    );
    if (level === "error") {
      Diagnostics.error(
        StandaloneMediaPlayerImpl.TAG,
        "stacktrace:,",
        optionalParams[0]
      );
    }
  }

  // ── Lifecycle ──────────────────────────────────────────────────────────────

  /** @inheritdoc */
  public init(mediaEl: HTMLVideoElement): void {
    if (this.mMediaEl !== null) {
      this.log("debug", "init() - already initialized, ignoring");
      return;
    }

    this.log("debug", "init()");
    this.mMediaEl = mediaEl;
    // Autoplay stays off: playback begins only from startPlayback(), so an
    // alert can never be triggered by the element itself.
    this.mMediaEl.autoplay = false;
    this.mMediaEl.playsInline = true;
    this.mMediaEl.preload = "auto";
    this.mMediaEl.addEventListener(
      StandaloneMediaPlayerImpl.EVENT_ENDED,
      this.mEndedHandler
    );
    this.mMediaEl.addEventListener(
      StandaloneMediaPlayerImpl.EVENT_ERROR,
      this.mErrorHandler
    );
    this.mMediaEl.addEventListener(
      StandaloneMediaPlayerImpl.EVENT_PLAYING,
      this.mPlayingHandler
    );
  }

  /** @inheritdoc */
  public destroy(): void {
    if (this.mMediaEl === null) {
      return;
    }

    this.log("debug", "destroy()");
    const mediaEl: HTMLVideoElement = this.mMediaEl;
    this.stopPlayback();
    mediaEl.removeEventListener(
      StandaloneMediaPlayerImpl.EVENT_ENDED,
      this.mEndedHandler
    );
    mediaEl.removeEventListener(
      StandaloneMediaPlayerImpl.EVENT_ERROR,
      this.mErrorHandler
    );
    mediaEl.removeEventListener(
      StandaloneMediaPlayerImpl.EVENT_PLAYING,
      this.mPlayingHandler
    );
    // The element belongs to the view, so it is released rather than removed.
    this.mMediaEl = null;
  }

  // ── Playback Control ───────────────────────────────────────────────────────

  /** @inheritdoc */
  public startPlayback(url: string): void {
    if (this.mMediaEl === null) {
      this.log("warn", "startPlayback() - not initialized, ignoring");
      return;
    }

    this.log("info", "startPlayback()");
    // Clear any in-flight source first so two tones can never overlap. This
    // reports no end of media, so a restart is not mistaken for one.
    this.stopPlayback();

    this.mCurrentUrl = url;
    // Cleared here because stopPlayback() leaves the element muted; see there
    // for why silence is the resting state of this element.
    this.mMediaEl.muted = false;
    this.mMediaEl.src = url;
    this.mMediaEl.load();

    // A rejected play() is reported as an ERROR event rather than thrown:
    // callers drive this from a notification handler and have nowhere to catch
    // it, and the UI needs the failure on the same channel as end of media so
    // it can leave the alert state either way.
    this.mMediaEl.play().catch((error: unknown) => {
      this.log("error", "startPlayback() - play() rejected", error);
      this.notifyError(StandaloneMediaPlayerImpl.describeError(error));
    });
  }

  /** @inheritdoc */
  public stopPlayback(): void {
    if (this.mMediaEl === null) {
      return;
    }

    // Deliberately not gated on `mCurrentUrl`. That field is this class's own
    // record of what it last started, and an element that is making sound is
    // the only thing that decides whether sound needs stopping. Any drift
    // between the two — a source that outlived its bookkeeping, a stop that
    // ran twice — used to leave an alert tone playing under a dismissed
    // alert. Pausing and resetting an already-idle element costs nothing, so
    // the question is simply never asked.
    this.log(
      "info",
      `stopPlayback() - paused=${String(this.mMediaEl.paused)}, currentUrl=${this.mCurrentUrl.length > 0}`
    );

    // Muted first, and left muted. Pausing and dropping the source should be
    // enough on their own, and on most engines they are — but this element
    // carries an emergency alert, and a tone still sounding over the screen
    // the viewer was just returned to is the one outcome worth spending a
    // property on to make impossible. Muting takes effect on the element
    // itself, so it holds even if playback somehow continues or resumes
    // underneath us. `startPlayback()` unmutes for the next alert.
    this.mMediaEl.muted = true;
    this.mMediaEl.pause();
    // Drop the source so the element holds no buffered data while idle.
    // removeAttribute() + load() is the portable reset; setting src = "" makes
    // some engines resolve the document URL and emit a spurious error.
    this.mMediaEl.removeAttribute("src");
    this.mMediaEl.load();
    this.mIsPlaying = false;
    this.mCurrentUrl = "";
  }

  // ── State ──────────────────────────────────────────────────────────────────

  /** @inheritdoc */
  public isPlaying(): boolean {
    return this.mIsPlaying;
  }

  // ── Events ─────────────────────────────────────────────────────────────────

  /** @inheritdoc */
  public getPlaybackEventSubscription(): ISubscription<TStandalonePlaybackEvent> {
    return this.mPlaybackEventSubscription;
  }

  /**
   * End of media — the source played through to its natural end.
   * Never fires for a stopPlayback() or a restart, both of which clear the
   * source without letting it finish.
   */
  private onEnded(_event: Event): void {
    this.log("info", "onEnded() - end of media");
    this.mIsPlaying = false;
    this.mPlaybackEventSubscription.notify({
      state: PlayerState.ENDED,
      url: this.mCurrentUrl
    });
  }

  private onPlaying(_event: Event): void {
    this.log("info", "onPlaying()");
    this.mIsPlaying = true;
    this.mPlaybackEventSubscription.notify({
      state: PlayerState.PLAYING,
      url: this.mCurrentUrl
    });
  }

  private onError(_event: Event): void {
    const message: string = this.describeMediaElementError();
    this.log("error", "onError() - " + message);
    this.notifyError(message);
  }

  /**
   * Report a failure. The UI treats this like an end of media, since a source
   * that failed will never raise `ended` and would otherwise strand the alert
   * on screen.
   *
   * @param errorMessage - Human-readable failure description
   */
  private notifyError(errorMessage: string): void {
    this.mIsPlaying = false;
    this.mPlaybackEventSubscription.notify({
      state: PlayerState.ERROR,
      url: this.mCurrentUrl,
      errorMessage: errorMessage
    });
  }

  /**
   * Describe the media element's current error.
   *
   * @returns The element's error message, or a default when none is readable
   */
  private describeMediaElementError(): string {
    // Typed MediaError | null, but absent as `undefined` on jsdom and on some
    // embedded engines — so this is a truthiness check, not a null check.
    if (this.mMediaEl === null || !this.mMediaEl.error) {
      return StandaloneMediaPlayerImpl.DEFAULT_ERROR_MESSAGE;
    }

    const mediaError: MediaError = this.mMediaEl.error;
    const detail: string =
      typeof mediaError.message === "string" && mediaError.message.length > 0
        ? mediaError.message
        : StandaloneMediaPlayerImpl.DEFAULT_ERROR_MESSAGE;
    return "code=" + String(mediaError.code) + ", " + detail;
  }

  /**
   * Describe a rejected play() promise.
   *
   * @param error - The rejection value, of unknown shape
   * @returns A human-readable description
   */
  private static describeError(error: unknown): string {
    if (error instanceof Error) {
      return error.message;
    }
    if (typeof error === "string" && error.length > 0) {
      return error;
    }
    return StandaloneMediaPlayerImpl.DEFAULT_ERROR_MESSAGE;
  }
}
